import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

/* ── Scroll fade-in hook ─────────────────────────────────── */

const useFadeIn = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('visible'); io.disconnect(); } },
      { threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

/* ── Keyframes ───────────────────────────────────────────── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── SVG Icons ───────────────────────────────────────────── */

const EmailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

/* ── Nav ─────────────────────────────────────────────────── */

const NavBar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  background: var(--nav-bg);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid var(--border-color);
  transform: translateY(-100%);
  transition: transform 0.35s ease;

  &[data-visible='true'] {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`;

const NavName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary-color);
  letter-spacing: -0.3px;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.75rem;
  align-items: center;

  @media (max-width: 480px) {
    gap: 1.25rem;
  }
`;

const NavLink = styled.a`
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--muted-color);
  letter-spacing: 0.01em;
  transition: color 0.2s;

  &:hover {
    color: var(--primary-color);
    opacity: 1;
  }

  @media (max-width: 480px) {
    display: none;

    &:last-child {
      display: block;
    }
  }
`;

/* ── Banner ─────────────────────────────────────────────── */

const Banner = styled.div`
  width: 100%;
  height: 340px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #334155 70%, #1e293b 100%);

  @media (max-width: 600px) {
    height: 240px;
  }
`;

const BannerPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background-image:
    radial-gradient(circle at 25% 25%, #fff 1px, transparent 1px),
    radial-gradient(circle at 75% 75%, #fff 1px, transparent 1px);
  background-size: 48px 48px;
`;

const BannerOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 65%;
  background: linear-gradient(to top, var(--background-color), transparent);
`;

/* ── Layout ─────────────────────────────────────────────── */

const Container = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 0 2.5rem 5rem;

  @media (max-width: 600px) {
    padding: 0 1.25rem 3.5rem;
  }
`;

const ThemeToggle = styled.button`
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  background: var(--background-color);
  border: 1px solid var(--border-color);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0.45rem 0.65rem;
  border-radius: 6px;
  z-index: 300;
  opacity: 0.65;
  transition: opacity 0.2s, box-shadow 0.2s, top 0.35s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  &[data-nav='true'] {
    top: calc(52px + 0.6rem);
  }

  &:hover {
    opacity: 1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
`;

/* ── Profile Header ─────────────────────────────────────── */

const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2.25rem;
  margin-top: -100px;
  position: relative;
  z-index: 10;
  margin-bottom: 2.75rem;
  animation: ${fadeIn} 0.7s ease both;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    margin-top: -70px;
    gap: 1.25rem;
    text-align: center;
  }
`;

const Portrait = styled.img`
  width: 200px;
  height: 260px;
  object-fit: cover;
  object-position: center 45%;
  border-radius: 10px;
  border: 3px solid var(--background-color);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 160px;
    height: 210px;
  }
`;

const ProfileInfo = styled.div`
  padding-bottom: 0.5rem;
`;

const Name = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--primary-color);
  letter-spacing: -0.8px;
  line-height: 1.1;
  margin-bottom: 0.3rem;
`;

const Tagline = styled.p`
  font-size: 0.875rem;
  color: var(--accent-color);
  font-weight: 500;
  letter-spacing: 0.01em;
  margin-bottom: 0.4rem;
`;

const Location = styled.p`
  font-size: 0.825rem;
  color: var(--muted-color);
  font-weight: 400;
  margin-bottom: 1rem;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--muted-color);
  border: 1px solid var(--border-color);
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  transition: color 0.2s, border-color 0.2s, background 0.2s;

  &:hover {
    color: var(--primary-color);
    border-color: var(--primary-color);
    background: var(--surface-color);
    opacity: 1;
  }
`;

/* ── Sections ───────────────────────────────────────────── */

const FadeSection = styled.div`
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.65s ease, transform 0.65s ease;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Section = styled.section`
  margin-bottom: 3.75rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.75rem;
`;

const SectionTitle = styled.h2`
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--muted-color);
  white-space: nowrap;
`;

const SectionLine = styled.div`
  flex: 1;
  height: 1px;
  background: var(--border-color);
`;

/* ── Text ────────────────────────────────────────────────── */

const P = styled.p`
  font-size: 0.925rem;
  line-height: 1.82;
  color: var(--text-color);
  margin-bottom: 0.85rem;

  &:last-child { margin-bottom: 0; }
`;

/* ── Project Cards ───────────────────────────────────────── */

const ProjectsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProjectCard = styled.article`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 1.75rem;
  transition: box-shadow 0.25s ease;

  &:hover {
    box-shadow: var(--card-shadow);
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 1rem;
`;

const ProjectName = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  letter-spacing: -0.2px;
`;

const BulletList = styled.ul`
  padding-left: 1.15rem;
  margin: 0.6rem 0 0.85rem;
`;

const BulletItem = styled.li`
  font-size: 0.885rem;
  line-height: 1.75;
  color: var(--text-color);
  margin-bottom: 0.3rem;

  &::marker { color: var(--muted-color); }
  &:last-child { margin-bottom: 0; }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 1.1rem;
`;

const Tag = styled.span`
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--tag-color);
  background: var(--tag-bg);
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  letter-spacing: 0.01em;
`;

/* ── Skills ─────────────────────────────────────────────── */

const SkillsTable = styled.dl`
  display: grid;
  grid-template-columns: 140px 1fr;
  row-gap: 0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SkillRow = styled.div`
  display: contents;

  & > dt,
  & > dd {
    padding: 0.85rem 0;
    border-bottom: 1px solid var(--border-color);
  }

  &:last-child > dt,
  &:last-child > dd {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--border-color);

    &:last-child {
      border-bottom: none;
    }

    & > dt,
    & > dd {
      padding: 0;
      border-bottom: none;
    }
  }
`;

const SkillLabel = styled.dt`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted-color);
  padding-right: 1.5rem;
  white-space: nowrap;
  align-self: start;
  padding-top: 0.85rem;
`;

const SkillValue = styled.dd`
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.6;
`;

/* ── Footer ─────────────────────────────────────────────── */

const Footer = styled.footer`
  margin-top: 4rem;
  padding: 3rem 0 0;
  border-top: 1px solid var(--border-color);
`;

const FooterCTA = styled.p`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--primary-color);
  letter-spacing: -0.4px;
  margin-bottom: 0.5rem;
`;

const FooterSub = styled.p`
  font-size: 0.875rem;
  color: var(--muted-color);
  line-height: 1.7;
  max-width: 480px;
  margin-bottom: 1.25rem;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const FooterLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.825rem;
  font-weight: 500;
  color: var(--muted-color);
  border: 1px solid var(--border-color);
  padding: 0.4rem 0.9rem;
  border-radius: 6px;
  transition: color 0.2s, border-color 0.2s, background 0.2s;

  &:hover {
    color: var(--primary-color);
    border-color: var(--primary-color);
    background: var(--surface-color);
    opacity: 1;
  }
`;

const Copyright = styled.p`
  font-size: 0.72rem;
  color: var(--muted-color);
  margin-top: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

/* ── App ────────────────────────────────────────────────── */

const FadeDiv: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useFadeIn();
  return <FadeSection ref={ref}>{children}</FadeSection>;
};

const App: React.FC = () => {
  const [theme, setTheme] = useState('light');
  const [navVisible, setNavVisible] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setNavVisible(window.scrollY > 280);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <NavBar data-visible={navVisible ? 'true' : 'false'}>
        <NavName>Daniel Abraham</NavName>
        <NavLinks>
          <NavLink href="#about">About</NavLink>
          <NavLink href="#projects">Projects</NavLink>
          <NavLink href="#skills">Skills</NavLink>
          <NavLink href="#contact">Contact</NavLink>
        </NavLinks>
      </NavBar>

      <ThemeToggle data-nav={navVisible ? 'true' : 'false'} onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')} aria-label="Toggle theme">
        {theme === 'light' ? '☾' : '☀'}
      </ThemeToggle>

      <Banner>
        <BannerPattern />
        <BannerOverlay />
      </Banner>

      <Container>

        <ProfileHeader>
          <Portrait src="/IMG_2806.jpeg" alt="Daniel Abraham" />
          <ProfileInfo>
            <Name>Daniel Abraham</Name>
            <Tagline>Solo Dev · ML &amp; Data Science Enthusiast</Tagline>
            <Location>Sublimest Global · Quebec, Canada</Location>
            <SocialRow>
              <SocialLink href="mailto:daniel@sublimest.ca" aria-label="Email">
                <EmailIcon /> Email
              </SocialLink>
            </SocialRow>
          </ProfileInfo>
        </ProfileHeader>

        <FadeDiv>
          <Section id="about">
            <SectionHeader>
              <SectionTitle>About</SectionTitle>
              <SectionLine />
            </SectionHeader>
            <P>
              I build production systems end-to-end — from data acquisition and pipeline engineering through ML model training to live, revenue-generating SaaS products. My work spans open-source intelligence platforms, Canadian real estate analytics, quantitative and high-frequency trading systems, and AI-powered legal tech.
            </P>
            <P>
              I specialize in problems where the data doesn't come from a clean API: reverse-engineering sources, building collection infrastructure, cleaning and normalizing messy multilingual datasets, and training ensemble ML models on top. Background in financial services. Based in Quebec, working through Sublimest Global.
            </P>
          </Section>
        </FadeDiv>

        <FadeDiv>
          <Section id="projects">
            <SectionHeader>
              <SectionTitle>Projects</SectionTitle>
              <SectionLine />
            </SectionHeader>

            <ProjectsGrid>

              <ProjectCard>
                <ProjectHeader>
                  <ProjectName>Real-Time Global Situational Awareness Platform</ProjectName>
                </ProjectHeader>
                <P>
                  Full-spectrum OSINT platform fusing 150+ live feeds — military aircraft (ADS-B), naval vessels (AIS), satellite thermal data (NASA FIRMS), cyber threat indicators, civil unrest, and financial signals — into a single operational picture.
                </P>
                <BulletList>
                  <BulletItem>AI-driven threat scoring with country instability index based on weighted signal convergence</BulletItem>
                  <BulletItem>LLM-synthesized intelligence briefs generated on demand</BulletItem>
                  <BulletItem>Interactive 3D WebGL globe with 35+ toggleable layers and real-time updates</BulletItem>
                </BulletList>
                <TagRow>
                  <Tag>WebGL</Tag><Tag>Supabase</Tag><Tag>Edge Functions</Tag>
                  <Tag>WebSockets</Tag><Tag>Tauri</Tag><Tag>LLMs</Tag>
                </TagRow>
              </ProjectCard>

              <ProjectCard>
                <ProjectHeader>
                  <ProjectName>Canadian Real Estate Price Prediction Platform</ProjectName>
                </ProjectHeader>
                <P>
                  End-to-end ML platform for Quebec real estate pricing, built on a proprietary dataset of 300,000+ bilingual listings collected via custom scraping infrastructure.
                </P>
                <BulletList>
                  <BulletItem>241 engineered features including DBSCAN geo-clustering, walkability scores, and temporal market dynamics</BulletItem>
                  <BulletItem>Weighted ensemble of XGBoost, LightGBM, Random Forest, and PyTorch — trained on GPU with temporal cross-validation</BulletItem>
                  <BulletItem>Live SaaS with per-prediction confidence intervals, model breakdowns, and Stripe billing</BulletItem>
                </BulletList>
                <TagRow>
                  <Tag>Python</Tag><Tag>XGBoost</Tag><Tag>LightGBM</Tag><Tag>PyTorch</Tag>
                  <Tag>FastAPI</Tag><Tag>Next.js</Tag><Tag>MongoDB</Tag><Tag>Stripe</Tag>
                </TagRow>
              </ProjectCard>

              <ProjectCard>
                <ProjectHeader>
                  <ProjectName>Quantitative & High-Frequency Trading Systems</ProjectName>
                </ProjectHeader>
                <P>
                  Suite of automated trading systems spanning prediction market arbitrage, options strategies, and high-frequency execution across multiple exchanges and asset classes.
                </P>
                <BulletList>
                  <BulletItem>HFT engine with sub-50ms order-to-fill latency via hybrid WebSocket/REST feeds, co-located on low-latency European VPS</BulletItem>
                  <BulletItem>Prediction market arbitrage across Binance and Polymarket: sum-to-one mispricing and 15-minute spot-vs-odds lag exploitation</BulletItem>
                  <BulletItem>Options trading infrastructure with real-time Greeks computation, volatility surface modeling, and automated spread execution</BulletItem>
                  <BulletItem>Institutional-grade risk stack: circuit breakers, daily loss limits, dynamic position sizing, atomic execution checks, emergency kill switch</BulletItem>
                  <BulletItem>Live monitoring: real-time P&amp;L dashboard, Telegram alerts, and per-strategy performance attribution</BulletItem>
                </BulletList>
                <TagRow>
                  <Tag>Python</Tag><Tag>WebSockets</Tag><Tag>Binance API</Tag>
                  <Tag>Polymarket</Tag><Tag>Redis</Tag><Tag>Options</Tag><Tag>HFT</Tag>
                </TagRow>
              </ProjectCard>

              <ProjectCard>
                <ProjectHeader>
                  <ProjectName>ClairContrat — AI Legal Contract Analyzer</ProjectName>
                </ProjectHeader>
                <P>
                  Quebec-focused platform where users upload French or English contracts and receive clause-by-clause breakdowns, obligation mapping, risk flags, and unfavorable term detection.
                </P>
                <BulletList>
                  <BulletItem>Handles Quebec-specific legal terminology and Civil Code context natively in French and English</BulletItem>
                  <BulletItem>Flags asymmetric liability, auto-renewal traps, termination penalties, and unilateral amendment rights</BulletItem>
                  <BulletItem>Full SaaS with auth, document history, PDF export, and Stripe-gated tiers</BulletItem>
                </BulletList>
                <TagRow>
                  <Tag>Next.js</Tag><Tag>TypeScript</Tag><Tag>LLM APIs</Tag>
                  <Tag>Vercel</Tag><Tag>Stripe</Tag>
                </TagRow>
              </ProjectCard>

            </ProjectsGrid>
          </Section>
        </FadeDiv>

        <FadeDiv>
          <Section id="skills">
            <SectionHeader>
              <SectionTitle>Technical Skills</SectionTitle>
              <SectionLine />
            </SectionHeader>
            <SkillsTable>
              <SkillRow>
                <SkillLabel>Languages</SkillLabel>
                <SkillValue>Python, TypeScript, JavaScript</SkillValue>
              </SkillRow>
              <SkillRow>
                <SkillLabel>Frontend</SkillLabel>
                <SkillValue>React, Next.js, WebGL, Tauri</SkillValue>
              </SkillRow>
              <SkillRow>
                <SkillLabel>Backend</SkillLabel>
                <SkillValue>FastAPI, Node.js, WebSockets, Supabase Edge Functions</SkillValue>
              </SkillRow>
              <SkillRow>
                <SkillLabel>ML / AI</SkillLabel>
                <SkillValue>XGBoost, LightGBM, PyTorch, scikit-learn, CUDA, LLM integration</SkillValue>
              </SkillRow>
              <SkillRow>
                <SkillLabel>Data</SkillLabel>
                <SkillValue>Pipeline orchestration, schema normalization, Isolation Forest, DBSCAN, feature engineering</SkillValue>
              </SkillRow>
              <SkillRow>
                <SkillLabel>Infra</SkillLabel>
                <SkillValue>Docker, Vercel, Railway, VPS, MongoDB, Redis</SkillValue>
              </SkillRow>
              <SkillRow>
                <SkillLabel>Domain</SkillLabel>
                <SkillValue>OSINT, HFT, options trading, quantitative finance, real estate ML, legal AI</SkillValue>
              </SkillRow>
            </SkillsTable>
          </Section>
        </FadeDiv>

        <FadeDiv>
          <Footer id="contact">
            <FooterCTA>Get in touch</FooterCTA>
            <FooterSub>
              Open to collaborations in real estate tech, quantitative finance, legal AI, and intelligence platforms.
            </FooterSub>
            <FooterLinks>
              <FooterLink href="mailto:daniel@sublimest.ca">
                <EmailIcon /> Email
              </FooterLink>
            </FooterLinks>
            <Copyright>&copy; {new Date().getFullYear()} Daniel Abraham · Sublimest Global</Copyright>
          </Footer>
        </FadeDiv>

      </Container>
    </>
  );
};

export default App;
